import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Row } from 'shards-react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';

import { templateActions } from '../../actions';
import PageTitle from '../../components/common/PageTitle';
import { educationTaxonomyService } from '../../services';
import { _getCategories } from './categoriesSize';

const fallbackGrades = _getCategories().map((category, index) => ({
  _id: `fallback-${index}`,
  type: 'grade',
  code: index === 0 ? 'preschool' : `grade-${index}`,
  title: category.name,
  active: true,
  order: index,
}));

const splitTags = value => String(value || '')
  .replace(/،/g, ',')
  .split(',')
  .map(tag => tag.trim())
  .filter(Boolean);

const NewTemplate = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const loading = useSelector(state => state.template.status);
  const templateId = useSelector(state => state.template.templateId);
  const group = useSelector(state => state.authentication.user.group);
  const [taxonomy, setTaxonomy] = useState([]);
  const [taxonomyMessage, setTaxonomyMessage] = useState('');
  const [gradeCode, setGradeCode] = useState('preschool');
  const [subjectCode, setSubjectCode] = useState('');
  const [topicCodes, setTopicCodes] = useState([]);
  const [createComplete, setCreateComplete] = useState(0);

  useEffect(() => {
    educationTaxonomyService.findAll({ active: true })
      .then(result => {
        const items = Array.isArray(result) ? result : [];
        setTaxonomy(items);
        const firstGrade = items.find(item => item.type === 'grade');
        if (firstGrade) setGradeCode(firstGrade.code);
      })
      .catch(error => setTaxonomyMessage(error.error || error.message || 'Using local grade definitions.'));
  }, []);

  useEffect(() => {
    if (createComplete === 1 && loading !== 'loading') setCreateComplete(2);
  }, [createComplete, loading]);

  const grades = useMemo(() => {
    const result = taxonomy.filter(item => item.type === 'grade' && item.active);
    return result.length ? result : fallbackGrades;
  }, [taxonomy]);
  const subjects = useMemo(() => taxonomy.filter(item => item.type === 'subject' && item.active
    && (!(item.gradeCodes || []).length || item.gradeCodes.includes(gradeCode))), [taxonomy, gradeCode]);
  const topics = useMemo(() => taxonomy.filter(item => item.type === 'topic' && item.active
    && (!subjectCode || item.subjectCode === subjectCode)
    && (!(item.gradeCodes || []).length || item.gradeCodes.includes(gradeCode))), [taxonomy, gradeCode, subjectCode]);

  useEffect(() => {
    if (subjectCode && !subjects.some(subject => subject.code === subjectCode)) setSubjectCode('');
    setTopicCodes(current => current.filter(code => topics.some(topic => topic.code === code)));
  }, [gradeCode, subjectCode, subjects, topics]);

  const formik = useFormik({
    initialValues: {
      name: '', price: '0', description: '', tags: '', tagsFa: '', learningObjective: '', estimatedMinutes: '15',
    },
    onSubmit: values => {
      const grade = grades.find(item => item.code === gradeCode);
      setCreateComplete(1);
      dispatch(templateActions.create({
        name: values.name,
        price: Number(values.price),
        invisible: true,
        size: { width: 1600, height: 1600 },
        description: values.description,
        tagsFa: splitTags(values.tagsFa),
        tags: splitTags(values.tags),
        group,
        categories: grade ? [grade.title] : [],
        education: {
          mode: 'lesson',
          gradeCode,
          subjectCode: subjectCode || undefined,
          topicCodes,
          learningObjective: values.learningObjective,
          estimatedMinutes: Number(values.estimatedMinutes) || undefined,
        },
      }));
    },
  });

  const toggleTopic = code => setTopicCodes(current => (
    current.includes(code) ? current.filter(item => item !== code) : [...current, code]
  ));

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle sm="4" title="Create Educational Template" subtitle="School" className="text-sm-left" />
      </Row>
      {taxonomyMessage && <div className="alert alert-warning">{taxonomyMessage}</div>}
      <form onSubmit={formik.handleSubmit}>
        <Row>
          <Col lg="4"><span>Name:</span><input id="templateName" name="name" required value={formik.values.name} onChange={formik.handleChange} /></Col>
          <Col><span>Description:</span><input id="description" name="description" value={formik.values.description} onChange={formik.handleChange} /></Col>
        </Row>
        <Row>
          <Col><span>Value:</span><select id="price" name="price" value={formik.values.price} onChange={formik.handleChange}><option value="0">Free</option><option value="1">Premium</option></select></Col>
          <Col><span>Grade:</span><select value={gradeCode} onChange={event => setGradeCode(event.target.value)}>{grades.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}</select></Col>
          <Col><span>Subject:</span><select value={subjectCode} onChange={event => setSubjectCode(event.target.value)}><option value="">Not assigned</option>{subjects.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}</select></Col>
          <Col><span>Canvas:</span><input value="1600 × 1600 px" readOnly /></Col>
        </Row>
        <Row>
          <Col><span>Learning objective:</span><input name="learningObjective" value={formik.values.learningObjective} onChange={formik.handleChange} /></Col>
          <Col lg="3"><span>Estimated minutes:</span><input type="number" min="1" max="600" name="estimatedMinutes" value={formik.values.estimatedMinutes} onChange={formik.handleChange} /></Col>
        </Row>
        {topics.length > 0 && (
          <Row><Col><span>Topics:</span><div>{topics.map(item => <label key={item.code} className="mr-3"><input type="checkbox" checked={topicCodes.includes(item.code)} onChange={() => toggleTopic(item.code)} /> {item.title}</label>)}</div></Col></Row>
        )}
        <Row>
          <Col><span>Tags (En):</span><textarea id="tags" name="tags" value={formik.values.tags} onChange={formik.handleChange} /></Col>
          <Col><span>Tags (Fa):</span><textarea id="tagsFa" name="tagsFa" value={formik.values.tagsFa} onChange={formik.handleChange} /></Col>
        </Row>
        <Row noGutters className="buttonHolder"><Col><Button type="submit" theme="success">Create</Button><Button type="button" theme="danger" onClick={() => history.push('/templates-list')}>Cancel</Button></Col></Row>
      </form>

      {createComplete === 2 && (
        <div className="vModal"><div className="vModalContent">
          <div className="vModalHeader">Template created successfully</div>
          <div className="vModalBody">Choose the next step.</div>
          <div className="vModalFooter">
            <Button theme="success" onClick={() => history.push('/templates-list')}>Start Design</Button>
            <Button theme="primary" onClick={() => history.push(`/template/${templateId}/edit`)}>Upload Resources</Button>
            <Button theme="primary" onClick={() => history.go(0)}>Create Another</Button>
            <Button theme="danger" onClick={() => history.push('/templates-list')}>Close</Button>
          </div>
        </div></div>
      )}
    </Container>
  );
};

export default NewTemplate;
